import { readNested } from '../helpers/assignfield';

/**
 * Get a single display label for a region object (BdsaRegion uses abbreviation, displayName).
 * Fallback order matches backend naming: abbreviation → displayName → name → longName.
 * @param {object|null} r - Region object
 * @returns {string} Display label or '—' if none
 */
export function getRegionDisplayLabel(r) {
    if (r == null || typeof r !== 'object') return '—';
    const label = r.abbreviation ?? r.displayName ?? r.name ?? r.longName;
    return label != null && String(label).trim() !== '' ? String(label) : '—';
}

/**
 * Generate form fields for a stain or region item based on its schema
 * This is shared logic used by Stains.js, Blocks.js, and the create modal
 */
export function makeStainFields(item) {
    const props = item.schema?.properties || [];
    const schemaFields = Object.entries(props).map(([key, val]) => {
        const field = {...val};
        field.required = item.schema?.required?.includes(key) === true;
        field.key = `details.${key}`;
        field.body = `details.${key}`;
        return field;
    });
    
    const name = {title: 'Name', type: 'string', required: true, query: 'name', key: 'name'};
    const usedFrom = {title: 'Used from', type: 'string', body: 'used_from', key: 'used_from'};
    const usedTo = {title: 'Used until', type: 'string', body: 'used_to', key: 'used_to'};
    const fields = [name, usedFrom, usedTo, ...schemaFields];
    
    // Set values from item if they exist
    for(const f of fields){
        if(f.key){
            f.value = readNested(item, f.key);
        }
    }
    
    return fields;
}

/**
 * Generate form fields for a block/region item
 * Uses the same logic as makeStainFields
 */
export function makeBlockFields(item) {
    return makeStainFields(item);
}

/**
 * Generate form fields for a region item
 * Includes abbreviation, displayName, and schema-based fields
 */
export function makeRegionFields(item, availableRegions = []) {
    const props = item.schema?.properties || [];
    const schemaFields = Object.entries(props).map(([key, val]) => {
        const field = {...val};
        field.required = item.schema?.required?.includes(key) === true;
        field.key = `details.${key}`;
        field.body = `details.${key}`;
        return field;
    });

    const abbreviation = {
        title: 'Abbreviation',
        type: 'string',
        required: true,
        query: 'abbreviation',
        body: 'abbreviation',
        key: 'abbreviation',
        description: 'Short code used in filenames (e.g., "MB", "FRCTX")'
    };

    const displayName = {
        title: 'Display name',
        type: 'string',
        required: true,
        body: 'display_name',
        key: 'displayName',
        description: 'Full descriptive name (e.g., "Midbrain")'
    };

    const usedFrom = {title: 'Used from', type: 'string', body: 'used_from', key: 'used_from'};
    const usedTo = {title: 'Used until', type: 'string', body: 'used_to', key: 'used_to'};

    // If availableRegions is provided, add schema region key (required for API)
    const regionSelector = availableRegions.length > 0 ? {
        title: 'Schema region',
        type: 'string',
        enum: availableRegions.map(r => r.key ?? r.title),
        body: 'schema_region_key',
        key: 'schemaRegionKey',
        description: 'Schema region this definition is for',
        value: item.schemaRegionKey || item._schemaRegion || ''
    } : null;

    const fields = [abbreviation, displayName, usedFrom, usedTo];
    if (regionSelector) {
        fields.splice(2, 0, regionSelector);
    }
    fields.push(...schemaFields);
    
    // Set values from item if they exist
    for(const f of fields){
        if(f.key){
            f.value = readNested(item, f.key);
        }
    }
    
    return fields;
}
